<template>
  <component
    :is="as"
    :type="type"
    :class="[classes, textSizeClass]"
    :disabled="disabled"
    v-bind="$attrs"
    role="button"
    v-on="$listeners"
  >
    <slot />
  </component>
</template>

<script>
import classes, { getModifierClass } from '~~/common/vue/mixins/classes';

export const elevations = ['0', '1', '2'];
export const sizes = ['xs', 's', 'm', 'l'];
export const textSizes = ['xs', 's', 'm', 'l'];
export const tags = ['button', 'a', 'nuxt-link', 'span'];
export const variants = [
  'bare',
  'solid-green',
  'solid-white',
  'outline',
];

export default {
  name: 'EBaseButton',
  mixins: [classes],
  props: {
    /**
     * `button, a, nuxt-link`
     */
    as: {
      type: String,
      default: 'button',
      validator: (value) => tags.includes(value),
    },
    type: {
      type: String,
      default: 'button',
    },
    disabled: {
      type: Boolean,
      default: false,
      cssClass: true,
    },
    /**
     * `bare, solid-white, solid-green, outline
     */
    variant: {
      type: String,
      default: 'solid-white',
      validator: (value) => variants.includes(value),
      cssClass: true,
    },
    /**
     * `s, m, l` The size of the button
     */
    size: {
      type: String,
      default: 'm',
      validator: (value) => sizes.includes(value),
      cssClass: true,
    },
    /**
     * `s, m, l` The size of the text (default: same as `size`)
     */
    textSize: {
      type: [String, null],
      validator: (value) => textSizes.includes(value),
      default: null,
    },
    /**
     * `0, 1, 2`
     */
    elevation: {
      type: String,
      default: '0',
      validator: (value) => elevations.includes(value),
      cssClass: true,
    },
    /**
     * Can be used to force the hover state in situations where
     * the button is only used decoratively inside an element which already
     * provides the intended interaction.
     */
    forceHover: {
      type: Boolean,
      default: false,
      cssClass: true,
    },
  },
  computed: {
    textSizeClass() {
      const realSize = this.textSize || this.size;
      return getModifierClass(this.baseClass, `text-size-${realSize}`);
    },
  },
};
</script>

<style lang="scss" scoped>
$sizes: (
  xs: (
    height: $space-l,
    padding: $space-s - $border-width,
  ),
  s: (
    height: $space-2l,
    padding: $space-s - $border-width,
  ),
  m: (
    height: $space-3l,
    padding: $space-m - $border-width,
  ),
  l: (
    height: $space-4l,
    padding: $space-l - $border-width,
  )
);

$font-sizes: (
  xs: $font-1s,
  s: $font-m,
  m: $font-l,
  l: $font-1l,
);

$variants: (
  bare: (
    color: var(--color-button-content-secondary),
    active-background-color: var(--color-button-background-transparent-active),
    hover-background-color: var(--color-button-background-transparent-hover),
  ),
  solid-green: (
    color: var(--color-button-content-primary),
    background-color: var(--color-button-background-primary),
    border-color: var(--color-button-background-primary),
    hover-background-color: var(--color-button-background-primary-hover),
    hover-border-color: var(--color-button-background-primary-hover),
    active-background-color: var(--color-button-background-primary-active),
    active-border-color: var(--color-button-background-primary-active),
  ),
  solid-white: (
    color: var(--color-button-content-secondary),
    background-color: var(--color-button-background-secondary),
    border-color: var(--color-button-background-secondary),
    hover-background-color: var(--color-button-background-secondary-hover),
    hover-border-color: var(--color-button-background-secondary-hover),
    active-background-color: var(--color-button-background-secondary-active),
    active-border-color: var(--color-button-background-secondary-active),
  ),
  outline: (
    color: var(--color-button-content-secondary),
    background-color: transparent,
    border-color: var(--color-button-border),
    hover-background-color: var(--color-button-background-transparent-hover),
    active-color: var(--color-button-tertiary-active),
    active-background-color: var(--color-button-background-transparent-active),
    background-color-icon: var(--color-button-background-secondary),
    active-background-color-icon: var(--color-button-background-secondary-active),
    hover-background-color-icon: var(--color-button-background-secondary-hover),
  ),
);

.base-button {
  -webkit-tap-highlight-color: transparent;
  position: relative;
  display: inline-flex;
  background-color: transparent;
  border-radius: $border-radius-full;
  font-family: $family;
  white-space: nowrap;
  text-align: center;
  justify-content: center;
  cursor: pointer;
  border: none;
  padding: 0;
  appearance: none;
  align-items: center;
  letter-spacing: 0.1px;
  transition: all $timing-2s $easing;

  &,
  &:hover,
  &:focus {
    text-decoration: none;
  }

  &:focus {
    @extend %keyboard-nav-focus;
  }
}

@each $name, $map in $sizes {
  .base-button--size-#{$name} {
    padding-left: map-get($map, padding);
    padding-right: map-get($map, padding);
    height: map-get($map, height);
    // the Button should always be at least square
    min-width: map-get($map, height);
  }
}

@each $size, $fs in $font-sizes {
  .base-button--text-size-#{$size} {
    font-size: $fs;
  }
}

@each $name, $map in $variants {
  $color: map-get($map, color);
  $background-color: map-get($map, background-color);
  $border-color: map-get($map, border-color);
  $hover-color: map-get($map, hover-color);
  $hover-background-color: map-get($map, hover-background-color);
  $hover-border-color: map-get($map, hover-border-color);
  $active-color: map-get($map, active-color);
  $active-background-color: map-get($map, active-background-color);
  $active-border-color: map-get($map, active-border-color);

  .base-button--variant-#{$name} {
    color: $color;
    background-color: $background-color;

    @if $border-color {
      border: $border-width solid $border-color;
    }

    &:hover,
    &.base-button--force-hover {
      color: $hover-color;
      background-color: $hover-background-color;

      @if $hover-border-color {
        border-color: $hover-border-color;
      }
    }

    &:active {
      color: $active-color;
      background-color: $active-background-color;

      @if $active-border-color {
        border-color: $active-border-color;
      }
    }

    // Background only for icon-button variant
    @if map-get($map, background-color-icon) {
      &.button-icon {
        background-color: map-get($map, background-color-icon);

        &:hover {
          background-color: map-get($map, hover-background-color-icon);
        }

        &:active {
          background-color: map-get($map, active-background-color-icon);
        }
      }
    }
  }
}

@each $name, $elevation in $elevations {
  .base-button--elevation-#{$name} {
    @include elevation($name);
  }
}

.base-button--disabled,
.base-button--disabled:hover,
.base-button--disabled:focus,
.base-button--disabled:active {
  color: var(--color-text-tertiary);
  background-color: var(--color-disabled);
  border-color: var(--color-disabled);
  cursor: default;
}
</style>
